import axios from 'axios';
import { API_URL, API_TIMEOUT, STORAGE_KEYS } from '../config/constants';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth headers
api.interceptors.request.use(
  async (config) => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
      return config;
    } catch (error) {
      console.error('Error in API interceptor:', error);
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`[API] Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error(`[API] Error: ${error.response?.status || 'Network Error'} ${error.config?.url}`);
    console.error('[API] Error details:', error.response?.data || error.message);
    
    // Handle 401 errors by clearing auth data
    if (error.response?.status === 401) {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
    
    return Promise.reject(error);
  }
);

// Auth Service
export const authService = {
  // Send OTP for login
  sendLoginOTP: async (mobile) => {
    return api.post('/auth/login-otp', { mobile });
  },

  // Send OTP for signup  
  sendSignupOTP: async (mobile, name) => {
    return api.post('/auth/signup-otp', { mobile, name });
  },

  // Verify OTP
  verifyOTP: async (mobile, otp, type = 'login') => {
    return api.post('/auth/verify-otp', { mobile, otp, type });
  },

  // Resend OTP
  resendOTP: async (mobile, type = 'login') => {
    return api.post('/auth/resend-otp', { mobile, type });
  },

  // Get current user
  getMe: async () => {
    return api.get('/auth/me');
  },

  // Update user details
  updateDetails: async (data) => {
    return api.put('/auth/updatedetails', data);
  }
};

// Menu Service (Public API for browsing, secure for ordering)
export const menuService = {
  // Get all menu items (public access for browsing)
  getAllItems: async () => {
    try {
      // Try with auth token first (for authenticated users)
      return await api.get('/menu');
    } catch (error) {
      // If auth fails, try without token for public browsing
      if (error.response?.status === 401) {
        console.log('[MenuService] Trying public menu access...');
        const publicApi = axios.create({
          baseURL: API_URL,
          timeout: API_TIMEOUT,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return await publicApi.get('/menu');
      }
      throw error;
    }
  },

  // Get single menu item (public access for viewing)
  getItem: async (id) => {
    try {
      // Try with auth token first
      return await api.get(`/menu/${id}`);
    } catch (error) {
      // If auth fails, try without token for public browsing
      if (error.response?.status === 401) {
        console.log('[MenuService] Trying public menu item access...');
        const publicApi = axios.create({
          baseURL: API_URL,
          timeout: API_TIMEOUT,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return await publicApi.get(`/menu/${id}`);
      }
      throw error;
    }
  }
};

// Toppings Service
export const toppingsService = {
  // Get all toppings (public access for browsing)
  getAllToppings: async () => {
    try {
      return await api.get('/toppings');
    } catch (error) {
      // If auth fails, try without token for public browsing
      if (error.response?.status === 401) {
        console.log('[ToppingsService] Trying public toppings access...');
        const publicApi = axios.create({
          baseURL: API_URL,
          timeout: API_TIMEOUT,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return await publicApi.get('/toppings');
      }
      throw error;
    }
  },

  // Get single topping
  getTopping: async (id) => {
    try {
      return await api.get(`/toppings/${id}`);
    } catch (error) {
      if (error.response?.status === 401) {
        const publicApi = axios.create({
          baseURL: API_URL,
          timeout: API_TIMEOUT,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return await publicApi.get(`/toppings/${id}`);
      }
      throw error;
    }
  }
};

// Orders Service
export const ordersService = {
  // Create new order
  createOrder: async (orderData) => {
    try {
      return await api.post('/orders', orderData);
    } catch (error) {
      console.error('[OrdersService] Create order error:', error);
      throw error;
    }
  },

  // Get user's orders
  getMyOrders: async () => {
    try {
      return await api.get('/orders/my');
    } catch (error) {
      console.error('[OrdersService] Get my orders error:', error);
      throw error;
    }
  },

  // Get order by ID
  getOrder: async (orderId) => {
    try {
      return await api.get(`/orders/${orderId}`);
    } catch (error) {
      console.error('[OrdersService] Get order error:', error);
      throw error;
    }
  }
};

// System Settings Service (for public settings like currency)
export const systemSettingsService = {
  // Get currency settings (public access for menu display)
  getCurrency: async () => {
    try {
      // First try to get from an admin/public endpoint if available
      const response = await api.get('/system-settings/currency');
      return response;
    } catch (error) {
      // Fallback to default currency if API fails
      console.warn('[SystemSettings] Currency API failed, using default:', error.message);
      return {
        data: {
          success: true,
          data: {
            code: 'INR',
            symbol: '₹',
            position: 'prefix'
          }
        }
      };
    }
  },

  // Get general settings including tax configuration
  getGeneralSettings: async () => {
    try {
      const response = await api.get('/system-settings/general');
      return response;
    } catch (error) {
      console.warn('[SystemSettings] General settings API failed, using defaults:', error.message);
      return {
        data: {
          success: true,
          data: {
            gstPercentage: 5.0,
            taxPercentage: 0.0,
            enableGST: true,
            enableTax: false,
            taxMethod: 'inclusive'
          }
        }
      };
    }
  },

  // Get public system settings (without authentication)
  getPublicSettings: async () => {
    try {
      // Create a request without auth headers for public data
      const publicApi = axios.create({
        baseURL: API_URL,
        timeout: API_TIMEOUT,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const response = await publicApi.get('/system-settings/public');
      return response;
    } catch (error) {
      console.warn('[SystemSettings] Public settings API failed, using defaults:', error.message);
      return {
        data: {
          success: true,
          data: {
            currency: {
              code: 'INR',
              symbol: '₹',
              position: 'prefix'
            }
          }
        }
      };
    }
  }
};



export default api; 